import { DeleteOutlined, EyeOutlined, EditOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  message,
  Pagination,
  Select,
  Table,
  DatePicker,
  Input,
  Space,
  Tag,
  Modal,
  Form,
  Row,
  Col,
  Tooltip,
} from "antd";
import axios from "axios";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import LoadingBox from "../../Components/LoadingBox";
import { API } from "../../Config";
import PageHeader from "../../Routes/PageHeader";
import moment from "moment";

const { Option } = Select;
const { RangePicker } = DatePicker;

function Complaints() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [users, setUsers] = useState([]);
  const [statusUpdateModal, setStatusUpdateModal] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [statusForm] = Form.useForm();
  const limit = 10;

  // Filter states
  const [filters, setFilters] = useState({
    userId: null,
    orderId: null,
    fromDate: null,
    toDate: null,
    title: null,
    status: null,
  });

  // Get user from Redux auth state
  const { user } = useSelector((state) => state.Auth);

  // Status options
  const statusOptions = [
    { value: "pending", label: "Pending", color: "orange" },
    { value: "process", label: "In Process", color: "blue" },
    { value: "resolved", label: "Resolved", color: "green" },
  ];

  // Headers function (assuming you have this utility)
  const Headers = () => ({
    Authorization: `Bearer ${user?.token}`,
    "Content-Type": "application/json",
  });

  // Fetch users for dropdown
  const fetchUsers = async () => {
    try {
      const response = await axios.get(
        API.BASR_URL + API.USERS_LIST + `/list`,
        {
          headers: Headers(),
        }
      );
      if (response.status === 200) {
        setUsers(response.data);
      }
    } catch (error) {
      message.error("Failed to fetch users");
    }
  };

  // Build query parameters
  const buildQueryParams = () => {
    const params = new URLSearchParams();
    params.append("limit", limit);
    params.append("page", page);

    if (filters.userId) params.append("userId", filters.userId);
    if (filters.orderId) params.append("orderId", filters.orderId);
    if (filters.fromDate) params.append("fromDate", filters.fromDate);
    if (filters.toDate) params.append("toDate", filters.toDate);
    if (filters.title) params.append("title", filters.title);
    if (filters.status) params.append("status", filters.status);

    return params.toString();
  };

  // Fetch complaints
  const fetchComplaints = async () => {
    setLoading(true);
    try {
      const queryParams = buildQueryParams();
      const response = await axios.get(
        `${API.BASR_URL}complaints?${queryParams}`,
        { headers: Headers() }
      );

      const { items, total: totalCount } = response.data.data;
      setData(items);
      setTotal(totalCount);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch complaints");
    } finally {
      setLoading(false);
    }
  };

  // Delete complaint
  const deleteComplaint = async (complaintId) => {
    Modal.confirm({
      title: "Are you sure?",
      content: "Do you want to delete this complaint?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await axios.delete(`${API.BASR_URL}complaints/${complaintId}`, {
            headers: Headers(),
          });
          message.success("Complaint deleted successfully!");
          fetchComplaints();
        } catch (err) {
          message.error(
            err.response?.data?.message || "Failed to delete complaint"
          );
        }
      },
    });
  };

  // Update complaint status
  const updateComplaintStatus = async (values) => {
    try {
      await axios.patch(
        `${API.BASR_URL}complaints/status/${selectedComplaint.id}`,
        { status: values.status },
        { headers: Headers() }
      );
      message.success("Status updated successfully!");
      setStatusUpdateModal(false);
      setSelectedComplaint(null);
      statusForm.resetFields();
      fetchComplaints();
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to update status");
    }
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1); // Reset to first page when filtering
  };

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setFilters((prev) => ({
        ...prev,
        fromDate: dates[0].format("YYYY-MM-DD"),
        toDate: dates[1].format("YYYY-MM-DD"),
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        fromDate: null,
        toDate: null,
      }));
    }
    setPage(1);
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      userId: null,
      orderId: null,
      fromDate: null,
      toDate: null,
      title: null,
      status: null,
    });
    setPage(1);
  };

  // Get status tag color
  const getStatusColor = (status) => {
    const statusOption = statusOptions.find((opt) => opt.value === status);
    return statusOption ? statusOption.color : "default";
  };

  // Get user name by ID
  const getUserName = (userId) => {
    const user = users.find((u) => u.id === userId);
    return user ? user.name : `User ${userId}`;
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchComplaints();
  }, [page, filters]);

  // Table columns configuration
  const columns = [
    {
      title: "#",
      key: "index",
      width: 60,
      render: (text, record, index) => (page - 1) * limit + index + 1,
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "date",
      width: 120,
      render: (text) => moment(text).format("DD/MM/YYYY"),
      sorter: (a, b) => moment(a.createdAt).unix() - moment(b.createdAt).unix(),
    },
    {
      title: "Time",
      dataIndex: "createdAt",
      key: "time",
      width: 100,
      render: (text) => moment(text).format("HH:mm"),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 200,
      ellipsis: {
        showTitle: false,
      },
      render: (title) => (
        <Tooltip placement="topLeft" title={title}>
          {title}
        </Tooltip>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 250,
      ellipsis: {
        showTitle: false,
      },
      render: (description) => (
        <Tooltip placement="topLeft" title={description}>
          {description}
        </Tooltip>
      ),
    },
    {
      title: "User",
      dataIndex: "userId",
      key: "user",
      width: 150,
      render: (userId) => getUserName(userId),
    },
    {
      title: "Order ID",
      dataIndex: "orderId",
      key: "orderId",
      width: 100,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => (
        <Tag
          color={getStatusColor(status)}
          style={{ textTransform: "capitalize" }}
        >
          {status}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Update Status">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setSelectedComplaint(record);
                statusForm.setFieldsValue({ status: record.status });
                setStatusUpdateModal(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => deleteComplaint(record.id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="container">
      <PageHeader
        PageName={"Complaints Management"}
        breadScrum={"Admin / Complaints"}
      />

      {/* Filters Section */}
      <Card className="mb-4" size="small">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6} lg={4}>
            <Select
              placeholder="Select User"
              allowClear
              value={filters.userId}
              onChange={(value) => handleFilterChange("userId", value)}
              style={{ width: "100%" }}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {users.map((user) => (
                <Option key={user.id} value={user.id}>
                  {user.name}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <Input
              placeholder="Order ID"
              value={filters.orderId}
              onChange={(e) => handleFilterChange("orderId", e.target.value)}
              allowClear
            />
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <Input
              placeholder="Search Title"
              value={filters.title}
              onChange={(e) => handleFilterChange("title", e.target.value)}
              allowClear
            />
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <Select
              placeholder="Status"
              allowClear
              value={filters.status}
              onChange={(value) => handleFilterChange("status", value)}
              style={{ width: "100%" }}
            >
              {statusOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  <Tag color={option.color}>{option.label}</Tag>
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <RangePicker
              style={{ width: "100%" }}
              onChange={handleDateRangeChange}
              value={
                filters.fromDate && filters.toDate
                  ? [moment(filters.fromDate), moment(filters.toDate)]
                  : null
              }
            />
          </Col>

          <Col xs={24} sm={12} md={4} lg={2}>
            <Button onClick={clearFilters} style={{ width: "100%" }}>
              Clear
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Content Section */}
      {loading ? (
        <LoadingBox />
      ) : error ? (
        <Card>
          <div className="text-center text-red-500 p-4">{error}</div>
        </Card>
      ) : data.length === 0 ? (
        <Card>
          <div className="text-center p-8">
            <p className="text-gray-500 text-lg">No complaints found</p>
          </div>
        </Card>
      ) : (
        <div>
          <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            pagination={false}
            scroll={{ x: 1200 }}
            size="small"
          />

          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="text-muted">
              Showing {(page - 1) * limit + 1} to{" "}
              {Math.min(page * limit, total)} of {total} entries
            </div>
            <Pagination
              current={page}
              pageSize={limit}
              total={total}
              onChange={(newPage) => setPage(newPage)}
              showSizeChanger={false}
              showQuickJumper
              size="small"
            />
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      <Modal
        title="Update Complaint Status"
        open={statusUpdateModal}
        onCancel={() => {
          setStatusUpdateModal(false);
          setSelectedComplaint(null);
          statusForm.resetFields();
        }}
        footer={null}
        width={400}
      >
        <Form
          form={statusForm}
          layout="vertical"
          onFinish={updateComplaintStatus}
        >
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: "Please select a status" }]}
          >
            <Select placeholder="Select Status">
              {statusOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  <Tag color={option.color}>{option.label}</Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item className="mb-0">
            <Space className="w-100 justify-content-end">
              <Button onClick={() => setStatusUpdateModal(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Update Status
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default Complaints;
